import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import styled from '@emotion/styled'
import {
  Button,
  Col,
  Dropdown,
  Row,
  Select,
  Space,
  Table,
  Tooltip,
  Typography,
  message
} from 'antd'
import type { ColumnsType } from 'antd/lib/table'
import { MoreOutlined } from '@ant-design/icons'
import { FilterIcon, SelectDownArrowIcon } from '@slerp/assets'
import { SelectPrefixWrapper } from 'components/Widgets'
import { useSession } from 'packages/@slerp/accounts'
import { GET_LOYALTY_MULTIPLIERS } from './queries'
import {
  GetLoyaltyMultipliersData,
  GetLoyaltyMultipliersVars,
  LoyaltyMultiplierListItem,
  LoyaltyMultiplierStatus,
  MultipliersFilter
} from './types'
import {
  appliesToLabel,
  archivedOnLabel,
  boostLabel,
  sortArchivedMultipliers,
  sortMultipliers,
  whenLabel,
  whenTooltip
} from './listUtils'
import { ArchiveMultiplierModal } from './ArchiveMultiplierModal'
import { MultiplierStatusTag } from './MultiplierStatusTag'
import { showArchivedToast } from './toasts'
import { useMultiplierActions } from './useMultiplierActions'
import {
  ARCHIVED_EMPTY_BODY,
  ARCHIVED_EMPTY_TITLE,
  ARCHIVED_ON_HEADER,
  FILTER_LABELS,
  RESTORE_BUTTON_LABEL,
  ROW_MENU_ARCHIVE,
  ROW_MENU_EDIT,
  ROW_MENU_PAUSE,
  ROW_MENU_RESUME,
  archiveErrorToast,
  pauseErrorToast,
  pausedToast,
  restoreErrorToast,
  restoredToast,
  resumeErrorToast,
  resumedToast
} from './wording'

const { Title, Paragraph } = Typography

const CREATE_PATH = '/loyalty/multipliers/create'

const EMPTY_BODY =
  'Multipliers are a great way of leveraging your existing loyalty programme to drive sales where they matter most to you. Promote new menu items or increase orders on slow days with a new Loyalty Multiplier'

const ERROR_BODY = "Couldn't load multipliers. Refresh the page to try again."

const baseColumns: ColumnsType<LoyaltyMultiplierListItem> = [
  { title: 'Name', dataIndex: 'name', key: 'name' },
  {
    title: 'Applies to',
    key: 'appliesTo',
    render: (_, item) => appliesToLabel(item)
  },
  {
    title: 'Loyalty card',
    key: 'loyaltyCard',
    render: (_, item) => item.loyaltyCard?.name ?? '—'
  },
  {
    title: 'When',
    key: 'when',
    render: (_, { days }) => {
      const tooltip = whenTooltip(days)
      const label = whenLabel(days)
      return tooltip ? <Tooltip title={tooltip}>{label}</Tooltip> : label
    }
  },
  {
    title: 'Boost',
    key: 'boost',
    render: (_, { factor }) => boostLabel(factor)
  }
]

interface EmptyStateProps {
  heading?: string
  body: string
}

const EmptyState = ({ heading, body }: EmptyStateProps) => (
  <EmptyCard>
    {heading && (
      <Title level={4} className='_mb-0'>
        {heading}
      </Title>
    )}
    <EmptyBody>{body}</EmptyBody>
  </EmptyCard>
)

export const MultipliersList = () => {
  const navigate = useNavigate()
  const { merchant } = useSession()

  const [filter, setFilter] = useState<MultipliersFilter>('published')
  const [restoringId, setRestoringId] = useState<string | null>(null)
  // Statuses returned by Pause/Resume, shown until the next load
  const [statusById, setStatusById] = useState<
    Record<string, LoyaltyMultiplierStatus>
  >({})
  const [archiveTarget, setArchiveTarget] =
    useState<LoyaltyMultiplierListItem | null>(null)
  const [archiving, setArchiving] = useState(false)
  const { setActive, archive, restore } = useMultiplierActions()
  const isArchived = filter === 'archived'

  const { data, loading, error, refetch } = useQuery<
    GetLoyaltyMultipliersData,
    GetLoyaltyMultipliersVars
  >(GET_LOYALTY_MULTIPLIERS, {
    fetchPolicy: 'no-cache',
    variables: { merchantId: merchant?.id || '', archived: isArchived }
  })

  const multipliers = useMemo(
    () =>
      (isArchived ? sortArchivedMultipliers : sortMultipliers)(
        data?.loyaltyMultipliers ?? []
      ),
    [data, isArchived]
  )

  useEffect(() => {
    setStatusById({})
  }, [data])

  const handleToggleActive = async ({
    id,
    name,
    status
  }: LoyaltyMultiplierListItem) => {
    const resuming = status === 'PAUSED'
    try {
      const result = await setActive(id, resuming)
      setStatusById((current) => ({ ...current, [id]: result.status }))
    } catch {
      message.error(resuming ? resumeErrorToast(name) : pauseErrorToast(name))
      return
    }
    message.success(resuming ? resumedToast(name) : pausedToast(name))
  }

  const handleArchive = async () => {
    if (!archiveTarget) return
    const { id, name } = archiveTarget
    setArchiving(true)
    try {
      await archive(id)
    } catch {
      message.error(archiveErrorToast(name))
      setArchiving(false)
      setArchiveTarget(null)
      return
    }
    setArchiving(false)
    setArchiveTarget(null)
    // A failed refetch shows the list's error state through useQuery
    await refetch().catch(() => undefined)
    showArchivedToast(name, () => setFilter('archived'))
  }

  const handleRestore = async ({ id, name }: LoyaltyMultiplierListItem) => {
    setRestoringId(id)
    try {
      await restore(id)
    } catch {
      message.error(restoreErrorToast(name))
      setRestoringId(null)
      return
    }
    message.success(restoredToast(name))
    // A failed refetch shows the list's error state through useQuery
    await refetch().catch(() => undefined)
    setRestoringId(null)
  }

  const publishedColumns: ColumnsType<LoyaltyMultiplierListItem> = [
    ...baseColumns,
    {
      title: 'Status',
      key: 'status',
      render: (_, { id, status }) => (
        <MultiplierStatusTag status={statusById[id] ?? status} />
      )
    },
    {
      key: 'actions',
      render: (_, item) => {
        const status = statusById[item.id] ?? item.status
        return (
          // The menu renders in a portal but its clicks still bubble here
          <div role='presentation' onClick={(event) => event.stopPropagation()}>
            <Dropdown
              trigger={['click']}
              menu={{
                items: [
                  { key: 'edit', label: ROW_MENU_EDIT },
                  {
                    key: 'toggle',
                    label:
                      status === 'PAUSED' ? ROW_MENU_RESUME : ROW_MENU_PAUSE
                  },
                  { key: 'archive', label: ROW_MENU_ARCHIVE }
                ],
                onClick: ({ key }) => {
                  if (key === 'edit')
                    navigate(`/loyalty/multipliers/${item.id}`)
                  if (key === 'toggle') {
                    handleToggleActive({ ...item, status })
                  }
                  if (key === 'archive') setArchiveTarget(item)
                }
              }}
            >
              <Button
                type='text'
                aria-label={`Actions for ${item.name}`}
                icon={<MoreOutlined />}
              />
            </Dropdown>
          </div>
        )
      }
    }
  ]

  const archivedColumns: ColumnsType<LoyaltyMultiplierListItem> = [
    ...baseColumns,
    {
      title: ARCHIVED_ON_HEADER,
      key: 'archivedOn',
      render: (_, { archivedAt }) =>
        archivedAt ? archivedOnLabel(archivedAt) : '—'
    },
    {
      key: 'restore',
      render: (_, item) => (
        <Button
          type='primary'
          ghost
          loading={restoringId === item.id}
          disabled={restoringId !== null && restoringId !== item.id}
          onClick={(event) => {
            event.stopPropagation()
            handleRestore(item)
          }}
        >
          {RESTORE_BUTTON_LABEL}
        </Button>
      )
    }
  ]

  const renderBody = () => {
    if (error) return <EmptyState body={ERROR_BODY} />
    if (!loading && multipliers.length === 0) {
      return isArchived ? (
        <EmptyState heading={ARCHIVED_EMPTY_TITLE} body={ARCHIVED_EMPTY_BODY} />
      ) : (
        <EmptyState heading='No Multipliers Yet' body={EMPTY_BODY} />
      )
    }
    return (
      <Table
        dataSource={multipliers}
        columns={isArchived ? archivedColumns : publishedColumns}
        rowKey='id'
        loading={loading}
        onRow={({ id }) => ({
          className: '_pointer',
          onClick: () => navigate(`/loyalty/multipliers/${id}`)
        })}
        pagination={{
          defaultPageSize: 100,
          position: ['bottomCenter'],
          showSizeChanger: true
        }}
      />
    )
  }

  return (
    <div data-testid='multipliers-list'>
      <Row justify='space-between' align='middle' className='_mt-16 _mb-32'>
        <Col>
          <Title level={3} className='_mb-0'>
            Multipliers
          </Title>
        </Col>
        <Col>
          <Space size={12}>
            <SelectPrefixWrapper prefixicon={<FilterIcon />}>
              <Select
                suffixIcon={<SelectDownArrowIcon />}
                value={filter}
                data-testid='multipliers-filter'
                onChange={setFilter}
              >
                <Select.Option value='published'>
                  {FILTER_LABELS.published}
                </Select.Option>
                <Select.Option value='archived'>
                  {FILTER_LABELS.archived}
                </Select.Option>
              </Select>
            </SelectPrefixWrapper>
            <Button type='primary' onClick={() => navigate(CREATE_PATH)}>
              SETUP MULTIPLIERS
            </Button>
          </Space>
        </Col>
      </Row>
      {renderBody()}
      <ArchiveMultiplierModal
        isOpen={archiveTarget !== null}
        name={archiveTarget?.name ?? ''}
        saving={archiving}
        onConfirm={handleArchive}
        onCancel={() => setArchiveTarget(null)}
      />
    </div>
  )
}

const EmptyCard = styled.div`
  margin-bottom: 32px;
  padding: 40px 24px;
  text-align: center;
  border: 1px solid #e7e5e9;
  border-radius: 6px;
`

const EmptyBody = styled(Paragraph)`
  max-width: 720px;
  margin: 16px auto 0;
`
